import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import z from 'zod';
import { getAccessToken } from './auth';
import config from '../config';
import models from '../models';

enum HttpMethod {
  GET = 'get',
  POST = 'post',
}

function extractResponseBody(data: unknown): string | null {
  if (typeof data === 'string') return data.length > 0 ? data : null;
  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    return JSON.stringify(data, null, 2);
  }
  return null;
}

function extractAxiosErrorMessage(err: AxiosError): string {
  const baseErrorData = `message: ${err.message}\nname: ${err.name}`;
  const body = extractResponseBody(err.response?.data);
  return body ? `:\n${baseErrorData}\n${body}` : `:\n${baseErrorData}`;
}

export const createCall = async <TResponse = unknown>(
  path: string,
  method: HttpMethod,
  ...params: [unknown?]
): Promise<TResponse> => {
  const [dataOrParams] = params;

  const accessToken = await getAccessToken();

  const apiClient = axios.create({
    baseURL: config.apiUrl,
    headers: accessToken
      ? {
          Authorization: `Bearer ${accessToken}`,
        }
      : undefined,
  });

  const axiosConfig: AxiosRequestConfig = {
    method,
    url: path,
  };

  if (method === HttpMethod.GET && dataOrParams) {
    axiosConfig.params = dataOrParams;
  } else if (method === HttpMethod.POST && dataOrParams) {
    axiosConfig.data = dataOrParams;
  }

  try {
    const { data } = await apiClient.request<TResponse>(axiosConfig);
    return data;
  } catch (err) {
    let errMessage = '';
    if (axios.isAxiosError(err)) {
      errMessage = extractAxiosErrorMessage(err);
    }
    throw new Error(`API request failed${errMessage}`);
  }
};

const runPlaybook = async (
  input: z.infer<typeof models.api.runPlaybookInput>
) =>
  createCall<z.infer<typeof models.api.runPlaybookOutput>>(
    '/playbook/execution',
    HttpMethod.POST,
    input
  );

const runPlaybookBatch = async (
  input: z.infer<typeof models.api.runPlaybookBatchInput>
) =>
  createCall<z.infer<typeof models.api.runPlaybookBatchOutput>>(
    '/playbook/execution/batch',
    HttpMethod.POST,
    input
  );

const getFiredAlerts = async (
  input: z.infer<typeof models.api.getFiredAlertsInput>
) =>
  createCall<z.infer<typeof models.api.getFiredAlertsOutput>>(
    '/alerts/fired',
    HttpMethod.GET,
    input
  );

const testAction = async (input: z.infer<typeof models.api.testActionInput>) =>
  createCall<z.infer<typeof models.api.testActionOutput>>(
    '/playbook/action/test',
    HttpMethod.POST,
    input
  );

const fetchTeleportContext = async (
  input: z.infer<typeof models.api.fetchTeleportContextInput>
) => createCall('/teleport/context', HttpMethod.GET, input);

const getAlert = async (input: z.infer<typeof models.api.getAlertInput>) =>
  createCall<z.infer<typeof models.api.getAlertOutput>>(
    '/alert',
    HttpMethod.GET,
    input
  );

const getPlaybook = async (
  input: z.infer<typeof models.api.getPlaybookInput>
) => createCall(`/playbook/${encodeURIComponent(input.name)}`, HttpMethod.GET);

const listPlaybooks = async () => createCall('/playbook', HttpMethod.GET);

const postFindingsToEnrichmentThread = async (
  input: z.infer<typeof models.api.postFindingsToEnrichmentThreadInput>
) =>
  createCall<z.infer<typeof models.api.postFindingsToEnrichmentThreadOutput>>(
    '/teleport/findings',
    HttpMethod.POST,
    input
  );

const getTuningCases = async (
  input: z.infer<typeof models.api.getTuningCasesInput>
) =>
  createCall<z.infer<typeof models.api.getTuningCasesOutput>>(
    '/cases',
    HttpMethod.GET,
    input.query
  );

const getReliabilityCenterAccess = async () =>
  createCall<z.infer<typeof models.api.reliabilityCenterAccessOutput>>(
    '/feature/reliability-center',
    HttpMethod.GET
  );

const runPlaybooks = async (
  input: z.infer<typeof models.api.runPlaybookBatchInput>
) => {
  const { inputList, bookYamlStr, bookName, verbose } = input;
  if (inputList.length === 1) {
    return runPlaybook({
      ...inputList[0],
      bookYamlStr,
      bookName,
      verbose,
    });
  }

  return runPlaybookBatch({ inputList, bookYamlStr, bookName, verbose });
};

export default {
  runPlaybooks,
  getFiredAlerts,
  testAction,
  fetchTeleportContext,
  getAlert,
  getPlaybook,
  listPlaybooks,
  postFindingsToEnrichmentThread,
  getReliabilityCenterAccess,
  getTuningCases,
};
